import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { debounce } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button, Spinner } from 'reactstrap';
import InputField from 'src/components/Shared/FormFields/InputField';
import { useAppDispatch } from 'src/hooks/redux-hook';
import { getJobDetails } from 'src/redux/job-details/api';
import { clearState, updateJobDetailsData } from 'src/redux/job-details/slice';
import { handleErrorMessages } from 'src/helper/utils';

type FormState = {
  work_order_id: string,
};

const Search = () => {
  const dispatch = useAppDispatch();

  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const methods = useForm<FormState>({
    mode: 'all',
    defaultValues: {
      work_order_id: '',
    },
  });

  const { handleSubmit, setValue } = methods;

  const fetchJobDetails = useCallback(
    (workOrderId:string) => {
      setIsFetchingDetails(true);
      dispatch(clearState());
      dispatch(getJobDetails(workOrderId)({}))
        .unwrap()
        .then((res) => {
          if (res) {
            dispatch(updateJobDetailsData({ workOrderData: res }));
          }
        })
        .catch((error) => {
          dispatch(clearState());
          handleErrorMessages({
            errors: error?.data?.errors || error?.message,
          }, (msg) => toast(msg, { type: 'error', theme: 'colored' }));
        })
        .finally(() => setIsFetchingDetails(false));
    },
    [dispatch],
  );

  const queryWorkOrderId = useMemo(
    () => new URLSearchParams(window.location.search).get('workOrderId'),
    [],
  );

  const debouncedHandleGetDetails = useMemo(
    () => debounce((workOrderId) => {
      if (workOrderId) {
        fetchJobDetails(workOrderId);
      } else {
        dispatch(clearState());
      }
    }, 500),
    [fetchJobDetails, dispatch],
  );

  const handleGetDetails = useCallback((formData:FormState) => {
    const workOrderId = formData?.work_order_id?.trim();
    debouncedHandleGetDetails(workOrderId);
  }, [debouncedHandleGetDetails]);

  const customInputHandle = useCallback(
    (event:React.ChangeEvent<HTMLInputElement>) => {
      if (!event?.target?.value) {
        dispatch(clearState());
      }
      return event;
    },
    [dispatch],
  );

  useEffect(() => () => {
    debouncedHandleGetDetails.cancel();
  }, [debouncedHandleGetDetails]);

  useEffect(() => {
    if (queryWorkOrderId) {
      setValue('work_order_id', queryWorkOrderId);
      handleGetDetails({ work_order_id: queryWorkOrderId });
    }
  }, [handleGetDetails, queryWorkOrderId, setValue]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleGetDetails)}>
        <div className="search-wrapper">
          <InputField
            type="search"
            name="work_order_id"
            placeholder="Search by Job UUID"
            className="search"
            customHandleChange={customInputHandle}
          />
          <Button
            type="submit"
            color="dark"
            className="ms-2 mb-3"
            disabled={isFetchingDetails}
          >
            {isFetchingDetails ? <Spinner color="light" size="sm" className="me-2" /> : ''}
            Get Details
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default Search;
